import { getGuestComponent } from '@apps/websiteCustomJsonComponents/config.guest';
import type { JsonDataBlobComponentFragment } from '../../../../../../../admin/routes/WebsiteEditor/features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import React, { Suspense, useEffect, useState } from 'react';
import type { GuestSiteComponentProps } from '@apps/websiteCustomJsonComponents/JsonComponent.types';
import { addError } from '@shared/utils/logger';
import { EmbeddedPostRsvpFunnelProvider } from '@shared/components/EventForm/components/functional/postRsvp/embeddedPostRsvpFunnelSlot';
import { LazyEmbeddedPostRsvpFunnel } from '@apps/guest/postRsvp/embedded/EmbeddedPostRsvpFunnel.lazy';
import { useIsNewStackEvent } from '@shared/utils/eventInfo/useIsNewStackEvent';

type GuestJsonComponentWrapperProps = {
  jsonComponentData: JsonDataBlobComponentFragment;
  pages?: readonly EventPageFragment[];
};

type LoadedComponentState = Readonly<{
  Component?: React.ComponentType<GuestSiteComponentProps>;
  isLoading: boolean;
}>;

export const GuestSiteCustomJsonComponentWrapper = (props: GuestJsonComponentWrapperProps) => {
  const { jsonComponentData, pages } = props;
  const data = jsonComponentData.containerContent;
  // One state, written once. The component and the loading flag used to be two `useState`s set in
  // the same async continuation, and React 17 does not batch updates outside its own event handlers
  // — so each custom component committed twice on resolve. A page with nine of them produced
  // eighteen commits during load, each its own layout and paint opportunity.
  const [{ Component, isLoading }, setLoadedComponent] = useState<LoadedComponentState>({ isLoading: true });
  // Only a new-stack wedding runs the funnel, so only it is given one: on every other event the end
  // screen has no funnel in its tree and the chunk is never fetched.
  const isNewStackEvent = useIsNewStackEvent();

  useEffect(() => {
    let isMounted = true;

    const loadComponent = async () => {
      setLoadedComponent(previous => (previous.isLoading ? previous : { ...previous, isLoading: true }));
      try {
        const loadedComponent = await getGuestComponent(data?.schemaUrl, data?.type);
        if (isMounted) {
          setLoadedComponent({ Component: loadedComponent, isLoading: false });
        }
      } catch (error) {
        // Silently fail - component won't render if it fails to load
        addError(error, { context: 'GuestSiteCustomJsonComponentWrapper', operation: 'loadComponent', schemaUrl: data?.schemaUrl, componentType: data?.type });
        if (isMounted) {
          setLoadedComponent({ Component: undefined, isLoading: false });
        }
      }
    };

    void loadComponent();

    return () => {
      isMounted = false;
    };
  }, [data?.schemaUrl, data?.type]);

  if (isLoading || !Component) {
    return null;
  }

  return (
    // The guest website is the one surface that runs the post-RSVP funnel after an embedded form (MGT-132),
    // so it is the one that injects it.
    <EmbeddedPostRsvpFunnelProvider value={isNewStackEvent ? LazyEmbeddedPostRsvpFunnel : null}>
      <Suspense fallback={null}>
        <Component
          {...props}
          data={data}
          pages={pages}
        />
      </Suspense>
    </EmbeddedPostRsvpFunnelProvider>
  );
};
